import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { GiftIconComponent } from '../../../app/core/svg-icons/gift-icon.component';

const meta: Meta<GiftIconComponent> = {
  title: 'Atoms/Icons/GiftIcon',
  component: GiftIconComponent,
  parameters: {
    docs: {
      description: {
        component: 'A gift icon component. Perfect for gift cards, promotions, special offers, and gift-giving features. Note: This icon has white fill by default and is designed for dark backgrounds.',
      },
    },
  },
};

export default meta;
type Story = StoryObj<GiftIconComponent>;

export const Default: Story = {
  render: () => ({
    template: `
      <div style="background-color: #5C0A31; padding: 1rem; border-radius: 8px; display: inline-block;">
        <app-gift-icon></app-gift-icon>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Default gift icon on dark background as intended.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-gift-icon')).toBeTruthy();
  },
};

export const GiftCards: Story = {
  render: () => ({
    template: `
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1.5rem; max-width: 900px;">
        
        <div style="background: linear-gradient(135deg, #5C0A31 0%, #8B1538 100%); border-radius: 12px; padding: 2rem; color: white; position: relative; overflow: hidden;">
          <div style="position: absolute; top: -20px; right: -20px; width: 80px; height: 80px; background-color: rgba(255,255,255,0.1); border-radius: 50%;"></div>
          <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
            <app-gift-icon></app-gift-icon>
            <h3 style="margin: 0; font-size: 1.5rem;">Gavekort</h3>
          </div>
          <p style="margin: 0 0 1.5rem 0; opacity: 0.9;">
            Det perfekte gave til vinelskeren
          </p>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 1.25rem; font-weight: bold;">500 kr</span>
            <button style="background-color: white; color: #5C0A31; border: none; padding: 0.5rem 1rem; border-radius: 6px; font-weight: bold; cursor: pointer;">
              Køb gavekort
            </button>
          </div>
        </div>
        
        <div style="background: linear-gradient(135deg, #C9A96E 0%, #B8956A 100%); border-radius: 12px; padding: 2rem; color: white; position: relative; overflow: hidden;">
          <div style="position: absolute; top: -20px; right: -20px; width: 80px; height: 80px; background-color: rgba(255,255,255,0.1); border-radius: 50%;"></div>
          <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
            <app-gift-icon></app-gift-icon>
            <h3 style="margin: 0; font-size: 1.5rem;">Premium Gavekort</h3>
          </div>
          <p style="margin: 0 0 1.5rem 0; opacity: 0.9;">
            Eksklusive vine og oplevelser
          </p>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 1.25rem; font-weight: bold;">1.000 kr</span>
            <button style="background-color: white; color: #C9A96E; border: none; padding: 0.5rem 1rem; border-radius: 6px; font-weight: bold; cursor: pointer;">
              Køb gavekort
            </button>
          </div>
        </div>
        
        <div style="background: linear-gradient(135deg, #8B4513 0%, #A0522D 100%); border-radius: 12px; padding: 2rem; color: white; position: relative; overflow: hidden;">
          <div style="position: absolute; top: -20px; right: -20px; width: 80px; height: 80px; background-color: rgba(255,255,255,0.1); border-radius: 50%;"></div>
          <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
            <app-gift-icon></app-gift-icon>
            <h3 style="margin: 0; font-size: 1.5rem;">Vinsmagning</h3>
          </div>
          <p style="margin: 0 0 1.5rem 0; opacity: 0.9;">
            Oplevelse for to personer
          </p>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 1.25rem; font-weight: bold;">1.500 kr</span>
            <button style="background-color: white; color: #8B4513; border: none; padding: 0.5rem 1rem; border-radius: 6px; font-weight: bold; cursor: pointer;">
              Book oplevelse
            </button>
          </div>
        </div>
        
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Gift icons used in gift card offerings with different price tiers.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-gift-icon')).toBeTruthy();
  },
};

export const SpecialOffers: Story = {
  render: () => ({
    template: `
      <div style="max-width: 600px;">
        <h3 style="margin-bottom: 1.5rem; color: #5C0A31;">Særlige tilbud</h3>
        
        <div style="display: flex; flex-direction: column; gap: 1rem;">
          
          <div style="border: 2px solid #C9A96E; border-radius: 8px; padding: 1.5rem; background: linear-gradient(to right, #5C0A31, #8B1538); color: white; position: relative;">
            <div style="position: absolute; top: -10px; left: 1rem; background-color: #C9A96E; color: #5C0A31; padding: 0.25rem 0.75rem; border-radius: 12px; font-size: 0.75rem; font-weight: bold;">
              SÆRLIGT TILBUD
            </div>
            <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
              <app-gift-icon></app-gift-icon>
              <h4 style="margin: 0; color: white;">Køb 3, betal for 2</h4>
            </div>
            <p style="margin: 0 0 1rem 0; opacity: 0.9;">
              På alle vine fra Bordeaux-regionen. Automatisk rabat i kurven.
            </p>
            <button style="background-color: white; color: #5C0A31; border: none; padding: 0.5rem 1rem; border-radius: 4px; font-weight: bold; cursor: pointer;">
              Se udvalget
            </button>
          </div>
          
          <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; background-color: #F8F9FA;">
            <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
              <div style="background-color: #28A745; padding: 0.5rem; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                <app-gift-icon></app-gift-icon>
              </div>
              <h4 style="margin: 0; color: #5C0A31;">Gratis gave ved køb over 1.000 kr</h4>
            </div>
            <p style="margin: 0 0 1rem 0; color: #666;">
              Få et elegant vinglas sæt som gave når du handler for over 1.000 kr.
            </p>
            <span style="color: #28A745; font-weight: bold; font-size: 0.875rem;">
              Gaven tilføjes automatisk til din ordre
            </span>
          </div>
          
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Gift icons used in special promotional offers.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-gift-icon')).toBeTruthy();
  },
};

export const GiftWrapService: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; max-width: 500px;">
        <h3 style="margin: 0 0 1.5rem 0; color: #5C0A31;">Gavepakkning</h3>
        
        <div style="display: flex; flex-direction: column; gap: 1rem;">
          
          <label style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border: 1px solid #ddd; border-radius: 6px; cursor: pointer; transition: border-color 0.2s;"
                 onmouseover="this.style.borderColor='#5C0A31'" onmouseout="this.style.borderColor='#ddd'">
            <input type="radio" name="gift-wrap" style="margin: 0;">
            <div style="background-color: #5C0A31; padding: 0.5rem; border-radius: 4px; display: flex; align-items: center; justify-content: center;">
              <app-gift-icon></app-gift-icon>
            </div>
            <div style="flex: 1;">
              <strong>Standard gavepakkning</strong><br>
              <span style="color: #666; font-size: 0.875rem;">Elegant gavepapir og bånd</span>
            </div>
            <span style="color: #5C0A31; font-weight: bold;">29 kr</span>
          </label>
          
          <label style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border: 1px solid #ddd; border-radius: 6px; cursor: pointer; transition: border-color 0.2s;"
                 onmouseover="this.style.borderColor='#5C0A31'" onmouseout="this.style.borderColor='#ddd'">
            <input type="radio" name="gift-wrap" style="margin: 0;">
            <div style="background-color: #C9A96E; padding: 0.5rem; border-radius: 4px; display: flex; align-items: center; justify-content: center;">
              <app-gift-icon></app-gift-icon>
            </div>
            <div style="flex: 1;">
              <strong>Premium gavepakkning</strong><br>
              <span style="color: #666; font-size: 0.875rem;">Luksuriøs træboks med personlig besked</span>
            </div>
            <span style="color: #5C0A31; font-weight: bold;">99 kr</span>
          </label>
          
          <div style="margin-top: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; font-weight: bold; color: #333;">
              Gavebesked (valgfri)
            </label>
            <textarea style="width: 100%; padding: 0.75rem; border: 1px solid #ddd; border-radius: 4px; font-size: 1rem; min-height: 80px; resize: vertical;" 
                      placeholder="Skriv din personlige gavebesked her..."></textarea>
          </div>
          
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Gift icons used in gift wrapping service options.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-gift-icon')).toBeTruthy();
  },
};

export const HolidayPromotion: Story = {
  render: () => ({
    template: `
      <div style="background: linear-gradient(135deg, #0D4F3C 0%, #5C0A31 100%); color: white; padding: 3rem 2rem; border-radius: 12px; text-align: center; max-width: 600px; position: relative; overflow: hidden;">
        <div style="position: absolute; top: -50px; left: -50px; width: 100px; height: 100px; background-color: rgba(255,255,255,0.1); border-radius: 50%;"></div>
        <div style="position: absolute; bottom: -30px; right: -30px; width: 80px; height: 80px; background-color: rgba(255,255,255,0.1); border-radius: 50%;"></div>
        
        <div style="display: flex; justify-content: center; margin-bottom: 1.5rem;">
          <div style="transform: scale(1.5);">
            <app-gift-icon></app-gift-icon>
          </div>
        </div>
        
        <h2 style="margin: 0 0 1rem 0; font-size: 2rem;">Julekampagne</h2>
        <p style="margin: 0 0 2rem 0; font-size: 1.125rem; opacity: 0.9; line-height: 1.6;">
          Perfekte vine til julemiddagen og som gaver til dine kære. 
          Gratis levering på alle ordrer i december måned.
        </p>
        
        <div style="display: flex; gap: 1rem; justify-content: center; margin-bottom: 2rem;">
          <div style="text-align: center;">
            <div style="font-size: 2rem; font-weight: bold;">25%</div>
            <div style="font-size: 0.875rem; opacity: 0.8;">Rabat på gavekort</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 2rem; font-weight: bold;">GRATIS</div>
            <div style="font-size: 0.875rem; opacity: 0.8;">Gavepakkning</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 2rem; font-weight: bold;">0 kr</div>
            <div style="font-size: 0.875rem; opacity: 0.8;">Levering</div>
          </div>
        </div>
        
        <button style="background-color: white; color: #5C0A31; border: none; padding: 1rem 2rem; border-radius: 6px; font-weight: bold; font-size: 1.125rem; cursor: pointer; transition: transform 0.2s;"
                onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
          Shop julegaver nu
        </button>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Gift icon used in holiday promotion banner.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-gift-icon')).toBeTruthy();
  },
};

export const InShoppingCart: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; max-width: 400px;">
        <h3 style="margin: 0 0 1rem 0; color: #5C0A31;">Din kurv</h3>
        
        <div style="display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1.5rem;">
          
          <div style="display: flex; gap: 1rem; padding: 1rem; border: 1px solid #eee; border-radius: 4px;">
            <div style="width: 60px; height: 80px; background-color: #f8f9fa; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #666; font-size: 0.75rem;">
              Vin
            </div>
            <div style="flex: 1;">
              <h5 style="margin: 0 0 0.25rem 0;">Bordeaux Superior 2019</h5>
              <p style="margin: 0; color: #666; font-size: 0.875rem;">Frankrig • 750ml</p>
              <p style="margin: 0.5rem 0 0 0; color: #5C0A31; font-weight: bold;">199 kr</p>
            </div>
          </div>
          
        </div>
        
        <div style="border-top: 1px solid #eee; padding-top: 1rem;">
          
          <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; padding: 1rem; background-color: #F8F9FA; border-radius: 4px; border: 1px dashed #5C0A31;">
            <div style="background-color: #5C0A31; padding: 0.5rem; border-radius: 4px; display: flex; align-items: center; justify-content: center;">
              <app-gift-icon></app-gift-icon>
            </div>
            <div style="flex: 1;">
              <strong style="color: #5C0A31;">Tilføj gavepakkning</strong><br>
              <span style="color: #666; font-size: 0.875rem;">Gør din ordre til en gave</span>
            </div>
            <button style="background-color: #5C0A31; color: white; border: none; padding: 0.5rem 1rem; border-radius: 4px; cursor: pointer; font-size: 0.875rem;">
              Tilføj
            </button>
          </div>
          
          <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
            <span>Subtotal:</span>
            <span>199 kr</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 1rem;">
            <span>Levering:</span>
            <span>39 kr</span>
          </div>
          <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 1.125rem; color: #5C0A31; border-top: 1px solid #eee; padding-top: 0.5rem;">
            <span>Total:</span>
            <span>238 kr</span>
          </div>
          
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Gift icon used in shopping cart to promote gift wrapping service.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-gift-icon')).toBeTruthy();
  },
};
